// src/pages/PredictionDashboardPage.tsx
// v55.1 - OMEGA HIERARCHY: TIER > DATA > SIMULATION > VISUALIZATION (STRICT & VERIFIED)

import React, { useState, useEffect, useMemo } from 'react';
import {
  Container, Typography, Box, Paper, Grid,
  FormControl, Select, MenuItem, ToggleButton, ToggleButtonGroup,
  Button, Divider, CircularProgress, TextField, InputAdornment, Alert
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { format, parseISO, getQuarter, getYear } from 'date-fns';
import { es } from 'date-fns/locale';
import { useLocation, useNavigate } from 'react-router-dom';

// AUTH & SECURITY
import { useAuth } from '../context/AuthContext';

// Iconos
import InventoryIcon from '@mui/icons-material/Inventory';
import PeopleIcon from '@mui/icons-material/People';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search'; 
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

// Servicios y Tipos
import { 
  getGlobalForecast, 
  getForecastBreakdown, 
  ScenarioRow 
} from '../services/predictionService';
import { getRetrainingStatus } from '../services/diagnosisService'; 
import { ViewMode } from '../types/predictions';
import { supabase } from '../config/supabaseClient';

// Componentes
import GlobalPredictionChart, { ChartDataPoint } from '../components/PredictionDashboard/GlobalPredictionChart';
import BreakdownResultTable from '../components/PredictionDashboard/BreakdownResultTable';
import SystemBootLoader from '../components/Common/SystemBootLoader';
import FeatureLockedState from '../components/Common/FeatureLockedState';
import PlanningTrialPanel from '../modules/pricingJourney/components/PlanningTrialPanel';
import { currentOrigin, useBackTarget, withReturnTo } from '../modules/pricingJourney/navigation';
import PageHeader from '../modules/pricingJourney/components/PageHeader';
import PageSkeleton from '../modules/pricingJourney/components/Skeletons';
import MetricCard from '../modules/pricingJourney/components/MetricCard';
import { JourneyChip } from '../modules/pricingJourney/components/JourneyBasics';
import EmptyState from '../modules/pricingJourney/components/EmptyState';
import { StatusBlock } from '../modules/pricingJourney/components/JourneyBlocks';

type TimeAggregation = 'monthly' | 'quarterly' | 'yearly';

const PredictionDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoading: authLoading, pricingAccess } = useAuth(); // ACCESO AL TIER
  // PRC-027: «Volver» al origen; sin origen, a Overview.
  const back = useBackTarget('/margin-center/overview', 'Volver a Overview');
  const location = useLocation();
  
  // --- ESTADO LOCAL ---
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [viewMode, setViewMode] = useState<ViewMode>('products');
  const [timeAggregation, setTimeAggregation] = useState<TimeAggregation>('monthly');
  const [isMinLoading, setIsMinLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // --- QUERY 1: RETRAINING STATUS (Para detectar Estado 2 vs 3) ---
  const { data: trainingStatus, isLoading: loadingStatus } = useQuery({
    queryKey: ['retraining-status'],
    queryFn: getRetrainingStatus,
    // PRC-027: en prueba Planificación no consulta al servidor.
    enabled: pricingAccess?.state !== 'trial',
  });

  // --- QUERY 2: GLOBAL FORECAST (Macro) ---
  const { 
    data: serviceResponse, 
    isLoading: loadingGlobal, 
    isError, // AHORA SÍ LO USAREMOS
  } = useQuery({
    queryKey: ['globalForecast'],
    queryFn: getGlobalForecast,
    retry: 1,
    // Solo ejecutamos si el usuario tiene permiso, ahorrando llamadas
    // PRC-027: en prueba no se pide (no está incluida y el servidor lo rechaza).
    enabled: !!user?.tier_details?.feature_explainability && pricingAccess?.state !== 'trial'
  });

  const globalData = useMemo(() => serviceResponse?.data || [], [serviceResponse]);

  // --- QUERY 3: BREAKDOWN (Micro) ---
  const { data: breakdownData, isLoading: loadingBreakdown } = useQuery<ScenarioRow[]>({
    queryKey: ['forecastBreakdown', selectedMonth, viewMode],
    queryFn: () => getForecastBreakdown(selectedMonth, viewMode),
    enabled: !!selectedMonth && globalData.length > 0
  });

  // --- KPI CALCULATION (CRÍTICO: NO TOCAR) ---
  const impactKPIs = useMemo(() => {
    if (!globalData.length) return { projected: 0, base: 0, delta: 0, potential_uplift: 0 };
    
    const totalProjected = globalData.reduce((acc, curr) => acc + curr.total_revenue, 0);
    const totalBase = globalData.reduce((acc, curr) => acc + (curr.revenue_base || 0), 0);
    const totalPotential = globalData.reduce((acc, curr) => acc + (curr.revenue_potential || 0), 0);
    
    return {
      projected: totalProjected,
      base: totalBase,
      delta: totalProjected - totalBase,
      potential_uplift: totalPotential - totalProjected
    };
  }, [globalData]);

  // --- UI LOGIC ---
  const isTactical = useMemo(() => {
    const label = serviceResponse?.tier_limit_applied || '';
    return label.includes('90') || (globalData.length > 0 && globalData.length <= 4);
  }, [serviceResponse, globalData]);

  useEffect(() => {
    if (globalData.length > 0) {
      const availableDates = globalData.map(d => d.prediction_date);
      if (!selectedMonth || !availableDates.includes(selectedMonth)) {
        setSelectedMonth(availableDates[0]);
      }
    }
  }, [globalData, selectedMonth]);

  useEffect(() => {
    const timer = setTimeout(() => setIsMinLoading(false), 800);
    return () => clearTimeout(timer);
  }, []);

  const horizonLabel = isTactical ? 'Próximos 3 meses' : 'Próximo año';

  // --- PREPARACIÓN DATOS GRÁFICO (CRÍTICO: NO TOCAR) ---
  const chartData = useMemo(() => {
    if (isTactical || timeAggregation === 'monthly') {
       return {
         revenue: globalData.map(d => ({ 
           prediction_date: d.prediction_date, 
           value: d.total_revenue,
           value_base: d.revenue_base,
           value_potential: d.revenue_potential
         } as ChartDataPoint)),
         quantity: globalData.map(d => ({ 
           prediction_date: d.prediction_date, 
           value: d.total_quantity,
           value_base: d.quantity_base,
           value_potential: d.quantity_potential
         } as ChartDataPoint))
       };
    }
    
    // Lógica de agregación simple para Trimestral/Anual
    let processedData: { date: string, revenue: number, quantity: number }[] = [];
    if (timeAggregation === 'quarterly') {
        const groups: Record<string, { revenue: number, quantity: number, dateLabel: string }> = {};
        globalData.forEach(d => {
            const date = parseISO(d.prediction_date);
            const key = `${getYear(date)}-Q${getQuarter(date)}`;
            if (!groups[key]) groups[key] = { revenue: 0, quantity: 0, dateLabel: `Q${getQuarter(date)} ${getYear(date)}` };
            groups[key].revenue += d.total_revenue;
            groups[key].quantity += d.total_quantity;
        });
        processedData = Object.keys(groups).sort().map(k => ({ date: groups[k].dateLabel, revenue: groups[k].revenue, quantity: groups[k].quantity }));
    } else {
        const groups: Record<string, { revenue: number, quantity: number }> = {};
        globalData.forEach(d => {
            const year = getYear(parseISO(d.prediction_date)).toString();
            if (!groups[year]) groups[year] = { revenue: 0, quantity: 0 };
            groups[year].revenue += d.total_revenue;
            groups[year].quantity += d.total_quantity;
        });
        processedData = Object.keys(groups).sort().map(k => ({ date: `Año ${k}`, revenue: groups[k].revenue, quantity: groups[k].quantity }));
    }
    return {
        revenue: processedData.map(d => ({ prediction_date: d.date, value: d.revenue } as ChartDataPoint)),
        quantity: processedData.map(d => ({ prediction_date: d.date, value: d.quantity } as ChartDataPoint))
    };
  }, [globalData, isTactical, timeAggregation]);

  // =================================================================================
  // LÓGICA DE RENDERIZADO JERÁRQUICA (OMEGA DIRECTIVE)
  // =================================================================================

  // 0. LOADER INICIAL (Anti-Flicker)
  // PRC-027: en prueba, pantalla explicativa en lugar de una redirección silenciosa.
  if (pricingAccess?.state === 'trial') return <PlanningTrialPanel />;

  const isProfileLoading = user && !user.tier_details;
  // PRC-031: estructura al instante en lugar de una rueda.
  if (authLoading || isProfileLoading) {
    return <PageSkeleton eyebrow="Planificación" title="Planificación" variant="dashboard" />;
  }

  // 1. ESTADO DE BLOQUEO (TIER) - PRIORIDAD MÁXIMA
  const canAccessPredictions = user?.tier_details?.feature_explainability === true;
  
  if (!canAccessPredictions) {
      return (
          <Container maxWidth="xl" sx={{ mt: 10, mb: 30, minHeight: '60vh' }}>
               <FeatureLockedState 
                  title="Inteligencia Predictiva"
                  description="Desbloquea el motor de IA para proyectar demanda futura y optimizar márgenes con previsión."
                  features={['Horizonte a 3 meses (Growth)', 'Horizonte a 1 año (Autonomy)', 'Simulación de escenarios']}
               />
          </Container>
      );
  }

  // 2. LOADER DE DATOS (Solo si tiene acceso)
  const showLoader = loadingGlobal || isMinLoading || loadingStatus;
  if (showLoader) {
    return <SystemBootLoader isOpen={true} message="Analizando datos..." />;
  }

  // 2.5 ESTADO DE ERROR (MANEJO DE EXCEPCIÓN)
  if (isError) {
      return (
         <Container maxWidth="xl" sx={{ mt: 4 }}>
             <Box sx={{ display: 'flex', mb: 4 }}><Button startIcon={<ArrowBackIcon />} onClick={back.go} sx={{ color: '#94a3b8' }}>{back.label}</Button></Box>
             <Alert severity="error" variant="filled">
                No se pudo conectar con el Motor de Inteligencia. Por favor, intente recargar la página o contacte con soporte.
             </Alert>
         </Container>
      );
  }

  // 3. ESTADO VACÍO (NO HAY PREDICCIONES)
  if (!globalData || globalData.length === 0) {
      
      // SUB-CASO A: NO HAY DATOS CARGADOS (Estado 2)
      // Si el status es 'no_data', priorizamos enviar a Cargar Datos
      if (trainingStatus?.status === 'not_found') {
          // PRC-033: estado vacío común, con la cabecera de la sección.
          return (
             <Box sx={{ width: '100%', px: { xs: 2, sm: 3, lg: 4 }, pt: 3, pb: 6 }}>
                 <PageHeader eyebrow="Planificación" title="Planificación" back={{ label: back.label, onClick: back.go }} />
                 <EmptyState
                    icon={<CloudUploadIcon />}
                    title="Aún no hay datos para planificar"
                    text="Para prever demanda y facturación, primero carga tu histórico de ventas y productos."
                    actionLabel="Cargar datos"
                    onAction={() => navigate(withReturnTo('/data-upload', currentOrigin(location)))}
                 />
             </Box>
          );
      }

      // SUB-CASO B: HAY DATOS, PERO NO SE HA SIMULADO (Estado 3)
      // Si hay datos ('idle', 'completed', etc) pero no hay predicciones, es que falta ejecutar el dashboard
      // PRC-033: preparando la previsión, con el bloque de estado común.
      return (
         <Box sx={{ width: '100%', px: { xs: 2, sm: 3, lg: 4 }, pt: 3, pb: 6 }}>
             <PageHeader eyebrow="Planificación" title="Planificación" back={{ label: back.label, onClick: back.go }} />
             <Box sx={{ maxWidth: 720 }}>
                 <StatusBlock
                    variant="info"
                    chip={<JourneyChip tone="info">Programado · esta noche a las 2:00</JourneyChip>}
                    title="Estamos preparando tu previsión"
                    text={`Tu histórico de ventas está listo y tu plan ${(user?.tier_details as any)?.name || 'Growth'} incluye Planificación. La previsión de demanda y facturación se calcula en el proceso nocturno; mañana la verás aquí. No tienes que hacer nada.`}
                 />
             </Box>
         </Box>
      );
  }

  // 4. DASHBOARD COMPLETO (ESTADO 4 - READY)
  const currencyFormatter = (val: number) => val.toLocaleString('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
  const unitFormatter = (val: number) => Math.round(val).toLocaleString('es-ES');
  const DARK_PAPER_STYLE = { p: 3, height: 400, borderRadius: 2, bgcolor: '#1e293b', border: '1px solid #334155', color: 'white', display: 'flex', flexDirection: 'column' };


  // --- EXPORT HANDLER (CRÍTICO: NO TOCAR) ---
  const handleExportScenarios = async () => {
      try {
          setIsExporting(true);
          const { data: { session } } = await supabase.auth.getSession();
          const token = session?.access_token;
          if (!token) throw new Error("No hay sesión activa");

          const BACKEND_URL_HARDCODED = "https://proapivot-backend-service-396057423134.europe-southwest1.run.app";
          const envUrl = import.meta.env.VITE_API_URL; 
          const API_BASE = envUrl ? (envUrl.endsWith('/') ? envUrl.slice(0, -1) : envUrl) : BACKEND_URL_HARDCODED;
          
          const response = await fetch(`${API_BASE}/api/v1/predictions/export-raw`, {
              method: 'GET',
              headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
              }
          });

          if (!response.ok) {
               const errText = await response.text();
               throw new Error(`Error Backend (${response.status}): ${errText}`); 
          }

          const data = await response.json();
          if (!data || data.length === 0) {
              alert("No hay datos disponibles.");
              return;
          }

          // --- DEFINICIÓN DE COLUMNAS (ORDEN ESTRICTO) ---
          const headers = [
              "Fecha", "Producto ID", "Cliente ID", "Cliente Nombre", "Coste",
              "Margen BHS (%)", "Margen Estrategia (%)", "Margen Max IA (€)", 
              "Precio Estrategia", "Fiabilidad", "Volumen Base", "Volumen Estrategia",
              "Facturación Base", "Facturación Estrategia"
          ];

          // --- FORMATEADORES ---
          const fmtNum = (val: any) => {
              if (val === null || val === undefined) return "0,00";
              const num = Number(val);
              if (isNaN(num)) return "0,00";
              return num.toFixed(2).replace('.', ',');
          };

          const fmtTxt = (val: any) => {
              if (!val) return "";
              const str = String(val);
              return `"${str.replace(/;/g, ' ').replace(/"/g, '""')}"`;
          };

          const csvRows = [headers.join(";")]; // SEPARADOR EUROPEO

          data.forEach((row: any) => {
              const line = [
                  row.fecha,
                  fmtTxt(row.id_producto),
                  fmtTxt(row.id_cliente),
                  fmtTxt(row.nombre_cliente),
                  fmtNum(row.coste),
                  fmtNum(row.margen_bhs),
                  fmtNum(row.margen_estrategia),
                  fmtNum(row.margen_max_ia),
                  fmtNum(row.precio_estrategia),
                  fmtTxt(row.fiabilidad),
                  fmtNum(row.volumen_base),
                  fmtNum(row.volumen_estrategia),
                  fmtNum(row.facturacion_base),
                  fmtNum(row.facturacion_estrategia)
              ].join(";");
              
              csvRows.push(line);
          });

          // BOM para UTF-8 en Excel
          const BOM = "\uFEFF"; 
          const csvContent = BOM + csvRows.join("\n");
          
          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `Proapricing_Export_${new Date().toISOString().slice(0,10)}.csv`;
          document.body.appendChild(link);
          link.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(link);

      } catch (error) {
          console.error("Export failed:", error);
          alert("Error al exportar. Ver consola.");
      } finally {
          setIsExporting(false);
      }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 8, minHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
      
      {/* PRC-032: cabecera común; «Volver» solo con origen. */}
      <PageHeader
        eyebrow="Planificación"
        title="Planificación"
        description="Demanda y facturación previstas a partir de tu histórico de ventas."
        back="origin"
      />

      {/* PRC-033: indicadores con la tarjeta común (estilo Overview), sin iconos de color. */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0,1fr))' }, gap: 2, mb: 4 }}>
        <MetricCard
          label="Facturación prevista con precios recomendados"
          value={currencyFormatter(impactKPIs.projected)}
          note={`Sin cambios de precio: ${currencyFormatter(impactKPIs.base)}`}
        />
        <MetricCard
          label="Facturación adicional"
          value={`+${currencyFormatter(impactKPIs.delta)}`}
          change="Diferencia con la previsión sin cambios de precio"
          changeTone="up"
        />
        <MetricCard
          label="Potencial no aplicado"
          value={`+${currencyFormatter(impactKPIs.potential_uplift)}`}
          note="Lo que se ganaría con la estrategia más agresiva; se reserva como margen de seguridad."
        />
      </Box>

      {/* CHARTS */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={DARK_PAPER_STYLE}>
            <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#f8fafc' }}>Facturación prevista</Typography>
                <JourneyChip>{horizonLabel}</JourneyChip>
            </Box>
            <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                <GlobalPredictionChart data={chartData.revenue} isLoading={false} isError={false} valueFormatter={currencyFormatter} lineColor="#38bdf8" chartTitle="Facturación prevista" chartType={isTactical ? 'bar' : 'area'} />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={DARK_PAPER_STYLE}>
            <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#f8fafc' }}>Demanda prevista</Typography>
                 <JourneyChip>{horizonLabel}</JourneyChip>
            </Box>
            <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                <GlobalPredictionChart data={chartData.quantity} isLoading={false} isError={false} valueFormatter={unitFormatter} lineColor="#7dd3fc" chartTitle="Demanda prevista" chartType={isTactical ? 'bar' : 'area'} />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* CONTROLS */}
      <Paper elevation={0} sx={{ p: 1.5, mb: 3, bgcolor: 'rgba(56, 189, 248, 0.05)', border: '1px solid rgba(56, 189, 248, 0.1)', borderRadius: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700 }}>Inicio del periodo</Typography>
              </Box>
              <FormControl size="small">
                <Select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  sx={{ minWidth: 180, color: 'white', '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(56, 189, 248, 0.3)' }, fontSize: '0.9rem', fontWeight: 'bold' }}
                  MenuProps={{ PaperProps: { sx: { bgcolor: '#1e293b', border: '1px solid #334155', color: 'white' } } }}
                >
                  {globalData.map((d) => (
                    <MenuItem key={d.prediction_date} value={d.prediction_date} sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' }, '&.Mui-selected': { bgcolor: 'rgba(56, 189, 248, 0.1)' } }}>
                      {format(parseISO(d.prediction_date), 'MMMM yyyy', { locale: es })}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
          </Box>
          <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(56, 189, 248, 0.2)' }} />
          <Button variant="outlined" startIcon={isExporting ? <CircularProgress size={20} color="inherit"/> : <DownloadIcon />} onClick={handleExportScenarios} disabled={isExporting} sx={{ borderColor: '#334155', color: '#94a3b8', textTransform: 'none', height: 40, minWidth: 140, '&:hover': { borderColor: '#f8fafc', color: '#f8fafc', bgcolor: 'rgba(255,255,255,0.05)' } }}>
            {isExporting ? 'Generando…' : 'Exportar todo'}
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          {!isTactical && (
            <ToggleButtonGroup value={timeAggregation} exclusive onChange={(_, newVal) => newVal && setTimeAggregation(newVal)} size="small" sx={{ border: '1px solid #334155', borderRadius: 1, bgcolor: '#0f172a' }}>
                <ToggleButton value="monthly" sx={{ color: '#94a3b8', textTransform: 'none', px: 2, '&.Mui-selected': { color: '#38bdf8', bgcolor: 'rgba(56, 189, 248, 0.1)' } }}>Mensual</ToggleButton>
                <ToggleButton value="quarterly" sx={{ color: '#94a3b8', textTransform: 'none', px: 2, '&.Mui-selected': { color: '#38bdf8', bgcolor: 'rgba(56, 189, 248, 0.1)' } }}>Trimestre</ToggleButton>
                <ToggleButton value="yearly" sx={{ color: '#94a3b8', textTransform: 'none', px: 2, '&.Mui-selected': { color: '#38bdf8', bgcolor: 'rgba(56, 189, 248, 0.1)' } }}>Año</ToggleButton>
            </ToggleButtonGroup>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <TextField
              placeholder={`Buscar ${viewMode === 'products' ? 'producto' : 'cliente'}…`}
              variant="outlined" size="small" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ width: 250, mr: 2, '& .MuiOutlinedInput-root': { color: '#e2e8f0', bgcolor: '#0f172a', height: 40, '& fieldset': { borderColor: '#334155' }, '&:hover fieldset': { borderColor: '#475569' }, '&.Mui-focused fieldset': { borderColor: '#38bdf8' } } }}
              InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ color: '#64748b', fontSize: 18 }} /></InputAdornment>) }}
          />
          <ToggleButtonGroup value={viewMode} exclusive onChange={(_, newVal) => newVal && setViewMode(newVal)} size="small" sx={{ border: '1px solid #334155', borderRadius: 1, '.MuiToggleButton-root': { color: '#94a3b8', borderColor: '#334155', textTransform: 'none', px: 2, '&.Mui-selected': { color: '#f8fafc', bgcolor: '#334155' } } }}>
            <ToggleButton value="products"><InventoryIcon sx={{ mr: 1, fontSize: 18 }} /> Productos</ToggleButton>
            <ToggleButton value="customers"><PeopleIcon sx={{ mr: 1, fontSize: 18 }} /> Clientes</ToggleButton>
          </ToggleButtonGroup>
      </Paper>

      <BreakdownResultTable 
        data={breakdownData || []} 
        isLoading={loadingBreakdown} 
        viewMode={viewMode}
        currentDate={selectedMonth}
        searchTerm={searchTerm}
      />
    </Container>
  );
};

export default PredictionDashboardPage;